import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Cookies from "universal-cookie";
import { useTable, usePagination, useSortBy } from "react-table";
import CambiaEstado from "./Evaluador/CambiaEstado";
import Swal from "sweetalert2";
import InformesRepositorio from "./ObtenerInforme";
import CrearSolicitud from "./Modals/crear/CrearSolicitud";
import Avance from "./Evaluador/Avance";
import ResumenOm from "./CargaOm/ResumenOm";
import ResumenOmModal from "./CargaOm/ResumenModal";
import FichaTecnica from "./FichaTecnica/FichaTecnica";
import RepositorioEvaluador from "./Evaluador/RepositorioEvaluador";

const cookies = new Cookies();

function SolicitudesDirector({ correoDesencriptado }) {
  const [usuarioId, setUsuarioId] = useState(null);
  const [solicitud, setSolicitud] = useState([]);
  const [search, setSearch] = useState("");

  const basicAuthHeader =
    "Basic " + btoa("BasicAuth@icontec.org:yQLzqmR7eZSuAmQZUqhY");

  const urlendpoint =
    "https://suadesarrollo.azurewebsites.net/api/ObtenerQSolicitudesDirector";

  // 🔎 Obtener usuario
  useEffect(() => {
    if (!correoDesencriptado) return;

    const obtenerUsuario = async () => {
      try {
        const response = await fetch(
          `https://suadesarrollo.azurewebsites.net/api/obtenerIdUsuario?correo=${correoDesencriptado}`,
          { headers: { Authorization: basicAuthHeader } },
        );

        if (!response.ok) throw new Error("Error HTTP");
        const data = await response.json();

        if (!cookies.get("usuarioId")) {
          Swal.fire({
            title: `👋 Bienvenido ${data.nombre}`,
            text: "Estamos cargando tu información",
            icon: "success",
          }).then(() => {
            cookies.set("usuarioId", data.id, { path: "/" });
            cookies.set("id", data.id, { path: "/" });
            cookies.set("Nombre", data.nombre, { path: "/" });
            cookies.set("correo", data.correo_Electronico, { path: "/" });
            cookies.set("tipo_Usuario", data.tipo_Usuario, { path: "/" });
            cookies.set("institucionid", data.institucionId, { path: "/" });

            setUsuarioId(data.id);
          });
        } else {
          setUsuarioId(cookies.get("usuarioId"));
        }
      } catch (error) {
        console.error("Error usuario:", error);
        Swal.fire("Error", "No se pudo obtener el usuario", "error");
      }
    };

    obtenerUsuario();
  }, [correoDesencriptado]);

  // 🔄 Obtener solicitudes
  useEffect(() => {
    const showData = async () => {
      try {
        const response = await fetch(urlendpoint, {
          headers: { Authorization: basicAuthHeader },
        });

        const data = await response.json();

        if (Array.isArray(data)) {
          setSolicitud(data);
        } else if (Array.isArray(data.resultado)) {
          setSolicitud(data.resultado);
        } else {
          setSolicitud([]);
        }
      } catch (error) {
        console.error("Error al obtener datos:", error);
        setSolicitud([]);
      }
    };

    showData();
  }, []);

  // 🔍 Buscador
  const searcher = (e) => setSearch(e.target.value);

  const results = useMemo(() => {
    if (!Array.isArray(solicitud)) return [];
    return !search
      ? solicitud
      : solicitud.filter((dato) =>
          dato.nombreInstitucion?.toLowerCase().includes(search.toLowerCase()),
        );
  }, [search, solicitud]);

  const tipoUsuario = Number(cookies.get("tipo_Usuario"));

  // 📌 Columnas
  const columns = useMemo(() => {
    let baseColumns = [
      { Header: "N° Solicitud", accessor: "solicitud" },
      { Header: "Nombre", accessor: "nombreInstitucion" },
      { Header: "Nit", accessor: "nitInstitucion" },
      { Header: "Estado", accessor: "estado" },
      { Header: "Tipo Solicitud", accessor: "tipoSolicitud" },
    ];

    baseColumns.push(
      {
        Header: "Informe Autoevaluación",
        Cell: ({ row }) => (
          <Link
            to={`/InformeAuto/${row.original.institucionId}/${row.original.solicitud}`}
          >
            Informe Autoevaluación
          </Link>
        ),
      },

      {
        Header: "Anexos",
        Cell: ({ row }) => (
          <Link to={`/RepositorioEvaluador/${row.original.institucionId}`}>
            Anexos
          </Link>
        ),
      },
      {
        Header: "Cambio estado",
        Cell: ({ row }) => (
          <CambiaEstado
            idSolicitud={row.original.solicitud}
            idinstitucion={row.original.institucionId}
            estadoActualId={row.original.tipoEstadoId}
          />
        ),
      },
      {
        Header: "Informes PDF",
        Cell: ({ row }) => {
          const estado = row.original.estado?.toLowerCase();
          const bloqueados = [
            "cancelada",
            "en diligenciamiento",
            "en revision",
            "en revisión",
          ];

          return tipoUsuario !== 1 && !bloqueados.includes(estado) ? (
            <InformesRepositorio
              idSolicitud={row.original.solicitud}
              idinstitucion={row.original.institucionId}
            />
          ) : null;
        },
      },
      {
        Header: "Informe Evaluador",
        Cell: ({ row }) => (
          <Link
            to={`/InformeEvaluador/${row.original.institucionId}/${row.original.solicitud}`}
          >
            Informe Evaluador
          </Link>
        ),
      },

      {
        Header: "Resumen OM",
        Cell: ({ row }) => (
          <ResumenOmModal institucionId={row.original.institucionId} />
        ),
      },
      {
        Header: "Ficha Técnica",
        Cell: ({ row }) => (
          <FichaTecnica
            idSolicitud={row.original.solicitud}
            institucion={row.original.institucionId}
            tipoSolicitud={row.original.tipoSolicitudId}
            ano={row.original.ano}
          />
        ),
      },
    );

    return baseColumns;
  }, [tipoUsuario]);

  const tableInstance = useTable(
    { columns, data: results, initialState: { pageSize: 5 } },
    useSortBy,
    usePagination,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    state: { pageIndex },
    pageOptions,
  } = tableInstance;

  return (
    <div className="card shadow mb-4">
      <div className="card-header">
        <h6 className="m-0 font-weight-bold text-primary">Solicitudes</h6>
      </div>

      <div className="card-body">
        {tipoUsuario === 2 && (
          <div className="mb-3">
            <CrearSolicitud />
          </div>
        )}

        {/* Buscador */}
        <div className="row mb-3">
          <div className="col-12 col-md-6 col-lg-4">
            <input
              value={search}
              onChange={searcher}
              placeholder="Buscar por institución"
              className="form-control"
            />
          </div>
        </div>

        {/* Tabla responsive */}
        <div className="table-responsive">
          <table
            {...getTableProps()}
            className="table table-bordered table-hover align-middle mb-0"
            style={{ minWidth: "1400px" }}
          >
            <thead className="table-light">
              {headerGroups.map((hg) => (
                <tr {...hg.getHeaderGroupProps()}>
                  {hg.headers.map((col) => (
                    <th
                      {...col.getHeaderProps(col.getSortByToggleProps())}
                      style={{
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                        verticalAlign: "middle",
                      }}
                    >
                      {col.render("Header")}

                      <span className="ms-1">
                        {col.isSorted ? (col.isSortedDesc ? " 🔽" : " 🔼") : ""}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()}>
              {page.length > 0 ? (
                page.map((row) => {
                  prepareRow(row);

                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          style={{
                            whiteSpace: "nowrap",
                            verticalAlign: "middle",
                          }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={columns.length} className="text-center py-4">
                    No se encontraron solicitudes
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación responsive */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 mt-4">
          <button
            onClick={previousPage}
            disabled={!canPreviousPage}
            className="btn btn-secondary "
          >
            ← Anterior
          </button>

          <span className="text-center">
            Página <strong>{pageIndex + 1}</strong> de{" "}
            <strong>{pageOptions.length || 1}</strong>
          </span>

          <button
            onClick={nextPage}
            disabled={!canNextPage}
            className="btn btn-primary "
          >
            Siguiente →
          </button>
        </div>
      </div>
    </div>
  );
}

export default SolicitudesDirector;
